import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppState } from '../../../store';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { quotingClose, quotingStart } from '../../../store/quote-progress/quote-progress.actions';
import {
    selectQuickQuote,
    selectShipmentEditorActionsBusy,
    selectShipmentEditorDisplayQuotes,
    selectShipmentEditorId,
    selectShipmentEditorLoading,
    selectShipmentEditorValid, selectShipmentEditorViolations
} from '../../../store/shipment-editor/shipment-editor.state';
import {
    shipmentEditorDisplayEditor,
    shipmentEditorDisplayQuotes,
    shipmentEditorInvalidate,
    shipmentEditorLoadRequest,
    shipmentEditorSaveRequest, shipmentEditorSetMode
} from '../../../store/shipment-editor/shipment-editor.actions';
import { ShipmentService } from '../../../data/shipment.service';
import { selectQuotingProgressCurrent } from '../../../store/quote-progress/quote-progress.state';
import { ViolationsModalComponent } from '../violations-modal/violations-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ShipmentQuotingComponent } from '../shipment-quoting/shipment-quoting.component';
import { ShipmentEditorComponent } from '../shipment-editor/shipment-editor.component';
import { AsyncPipe } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { selectRouteDataParam } from '../../../store/router/router.state';

@Component({
    selector: 'app-shipment-preparation',
    templateUrl: './shipment-preparation.component.html',
    styleUrls: ['./shipment-preparation.component.scss'],
    imports: [ShipmentEditorComponent, ShipmentQuotingComponent, AsyncPipe, MatProgressSpinner]
})
export class ShipmentPreparationComponent implements OnInit, OnDestroy {

    displayQuoting$: Observable<boolean>;
    displayEditor$: Observable<boolean>;
    loading$: Observable<boolean>;
    editorValid$: Observable<boolean>;
    reloadAvailable$: Observable<boolean>;
    validQuotesAvailable: Observable<boolean>;
    shipmentId$: Observable<string>;
    busy$: Observable<boolean>;
    quoteProgress$: Observable<number>;
    quickQuoteMode$: Observable<boolean>;

    id: string;
    routeView = '';
    viewSubscription: Subscription;

    constructor(private store: Store<AppState>, private shipmentService: ShipmentService, private dialog: MatDialog) {
        this.quickQuoteMode$ = this.store.select(selectQuickQuote);
        this.displayQuoting$ = this.store.select(selectShipmentEditorDisplayQuotes);
        this.loading$ = this.store.select(selectShipmentEditorLoading);
        this.busy$ = combineLatest([ this.loading$, this.store.select(selectShipmentEditorActionsBusy) ]).pipe(
            map(([ loading, active ]) => loading || active)
        );
        this.editorValid$ = this.store.select(selectShipmentEditorValid);
        this.reloadAvailable$ = combineLatest([ this.loading$, this.editorValid$ ]).pipe(
            map(([ loading, valid ]) => !loading && !valid)
        );
        this.validQuotesAvailable = this.store.select(selectShipmentEditorId).pipe(
            filter(id => !!id),
            switchMap(id => this.shipmentService.observeValidQuoteCount(id)),
            map(count => count > 0)
        );
        this.shipmentId$ = this.store.select(selectShipmentEditorId);
        this.quoteProgress$ = this.store.select(selectQuotingProgressCurrent).pipe(
            filter(state => !!state),
            map((state) => state.progress)
        );
    }

    public menuTop$: Observable<string> = combineLatest([
        timer(0, 1000),
        fromEvent(window, 'resize').pipe(startWith(0))
    ]).pipe(
        map(() => {
            const filterRect = document.getElementsByClassName('top-toolbar')[0].getBoundingClientRect();
            return filterRect.bottom + 'px';
        }),
        distinctUntilChanged()
    );

    ngOnInit(): void {
        this.viewSubscription = this.store.select(selectRouteDataParam('view')).subscribe(view => {
            this.routeView = view;
        });
    }

    ngOnDestroy(): void {
        this.viewSubscription?.unsubscribe();
        this.store.dispatch(shipmentEditorInvalidate());
    }

    save(): void {
        this.store.dispatch(shipmentEditorSaveRequest());
    }

    reload(): void {
        this.store.dispatch(shipmentEditorLoadRequest({ id: this.id }));
    }

    startQuoting(): void {
        combineLatest([ this.shipmentId$, this.store.select(selectShipmentEditorViolations) ]).pipe(
            take(1)
        ).subscribe(( [id, violations ]) => {
            if (violations.length > 0) {
                this.dialog.open(ViolationsModalComponent, { data: { quote: true }});
            } else {
                this.store.dispatch(quotingStart({ id }));
                this.store.dispatch(shipmentEditorDisplayQuotes());
            }
        });
    }

    endQuoting(): void {
        this.store.dispatch(quotingClose({ id: this.id }));
    }

    showQuotes(): void {
        this.store.dispatch(shipmentEditorDisplayQuotes());
    }

    showEditor(): void {
        this.store.dispatch(shipmentEditorDisplayEditor());
    }

    switchQuickQuote($event): void {
        this.store.dispatch(shipmentEditorSetMode({ quickQuote: $event.checked }));
    }
}
